import { useState, useMemo } from 'react';
import { ArrowLeft, BookOpen, Download, Search, X, ChevronRight, Lock } from 'lucide-react';
import MANUAL_INDEX from '../data/manualIndex.json';

const HINTS = ['Secreto 1', 'Certificados', 'Disney', 'Setter', 'Comisiones', 'Liderazgo', 'Visa'];

function highlight(text, words) {
  if (!words.length) return text;
  const pattern = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  return text.split(pattern).map((part, i) =>
    pattern.test(part) ? <mark key={`hl-${i}-${part}`} className="bg-yellow-200 text-[#1A1A2E] rounded px-0.5">{part}</mark> : part
  );
}

function excerpt(text, words, maxLen = 200) {
  const lower = text.toLowerCase();
  const pos = words.reduce((best, w) => {
    const idx = lower.indexOf(w.toLowerCase());
    return idx !== -1 && (best === -1 || idx < best) ? idx : best;
  }, -1);
  if (pos === -1) return text.slice(0, maxLen) + (text.length > maxLen ? '...' : '');
  const start = Math.max(0, pos - 50);
  const end = Math.min(text.length, pos + maxLen - 50);
  return (start > 0 ? '...' : '') + text.slice(start, end) + (end < text.length ? '...' : '');
}

const PDF_URL = '/docs/manual-completo-travel-empire.pdf';
const VIEWER_URL = `https://docs.google.com/gview?url=${encodeURIComponent(typeof window !== 'undefined' ? `${window.location.origin}${PDF_URL}` : PDF_URL)}&embedded=true`;

const ManualCompletoPage = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return MANUAL_INDEX.filter(entry => {
      const haystack = `${entry.t} ${entry.c}`.toLowerCase();
      return words.every(w => haystack.includes(w));
    }).slice(0, 50).map(entry => ({ ...entry, words }));
  }, [query]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = PDF_URL;
    a.download = 'Manual_Completo_Travel_Empire.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div data-testid="manual-completo-page" className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <a
            href="/"
            data-testid="back-home"
            className="flex items-center gap-2 text-slate-500 hover:text-[#1D1D1B] font-medium text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Regresar
          </a>
          <div className="w-px h-5 bg-slate-200" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Lock className="h-4 w-4 text-[#c9a227] flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-[#1D1D1B] text-sm truncate">Manual Completo Travel Empire</p>
              <p className="text-slate-400 text-[10px] truncate">252 paginas · Manual + Libro + Plan compensacion + FAQ</p>
            </div>
          </div>
          <button
            onClick={() => setShowSearch(s => !s)}
            data-testid="toggle-search-btn"
            className={`flex items-center gap-2 font-medium px-3 py-2 rounded-lg transition-colors text-sm flex-shrink-0 ${showSearch ? 'bg-[#1A1A2E] text-white hover:bg-[#2a2a4e]' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
          >
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">{showSearch ? 'Cerrar' : 'Buscar'}</span>
          </button>
          <button
            onClick={handleDownload}
            data-testid="download-manual-completo"
            className="flex items-center gap-2 bg-[#c9a227] hover:bg-[#e0b835] text-[#0a0a14] font-medium px-4 py-2 rounded-lg transition-colors text-sm flex-shrink-0"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Descargar PDF</span>
          </button>
        </div>

        {showSearch && (
          <div data-testid="search-panel" className="border-t border-slate-200 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4 py-4">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Busca cualquier tema, concepto o palabra clave..."
                  autoFocus
                  data-testid="manual-search-input"
                  className="w-full py-3 pl-11 pr-10 border-[1.5px] border-slate-200 rounded-lg text-base text-[#1A1A2E] outline-none focus:border-[#45A0EE] bg-white"
                />
                {query && (
                  <button onClick={() => setQuery('')} data-testid="clear-search-btn" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {HINTS.map(h => (
                  <button key={h} onClick={() => setQuery(h)} data-testid={`hint-${h}`} className="bg-[#EBF5FB] text-[#185FA5] text-xs px-3 py-1.5 rounded-full cursor-pointer hover:bg-[#45A0EE] hover:text-white transition-colors">
                    {h}
                  </button>
                ))}
              </div>
              {query.trim() && (
                <div className="mt-3" data-testid="search-results">
                  <div className="text-[12px] text-slate-500 mb-2">{results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}</div>
                  <div className="max-h-[40vh] overflow-y-auto space-y-2 pr-1">
                    {results.length === 0 ? (
                      <div className="text-center py-6 text-slate-400 text-sm">No se encontraron resultados para &ldquo;{query}&rdquo;</div>
                    ) : (
                      results.map((r, i) => (
                        <div key={`${r.p}-${i}`} className="bg-white rounded-lg px-4 py-3 border-l-4 border-[#45A0EE] hover:border-[#80C753] hover:shadow-sm transition-all">
                          <div className="flex items-start justify-between gap-3 mb-1">
                            {r.c && <span className="text-[11px] font-bold text-[#45A0EE] uppercase tracking-wide">{r.c}</span>}
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-[#1A1A2E] rounded-full px-2.5 py-0.5 flex-shrink-0">
                              <ChevronRight className="h-3 w-3" />
                              Ir a Pag. {r.p}
                            </span>
                          </div>
                          <p className="text-[13px] text-slate-700 leading-relaxed">{highlight(excerpt(r.t, r.words), r.words)}</p>
                        </div>
                      ))
                    )}
                  </div>
                  {results.length > 0 && (
                    <p className="text-[11px] text-slate-400 mt-2 italic">Tip: usa el control de pagina del visor (abajo) para saltar al numero indicado.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <div className="flex-1">
        <iframe
          src={VIEWER_URL}
          className="w-full border-0"
          style={{ minHeight: 'calc(100vh - 56px)', height: 'calc(100vh - 56px)' }}
          title="Manual Completo Travel Empire"
        />
      </div>
    </div>
  );
};

export default ManualCompletoPage;
